// @flow
import React, {useEffect, useState} from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {loadStripe} from '@stripe/stripe-js';
import {useFormatPriceAmount} from 'docs/hooks';
import {useAccount, useApi} from 'docs/data';
import Icon from 'sail/Icon';
import {Heading, Body} from 'sail/Text';
import Group from 'sail/Group';
import Box from 'sail/Box';
import {Button} from 'sail/Button';

import {useCondition} from 'docs/data/integration-builder';
import {PRICE as DEFAULT_PRICE} from 'docs/data/integration-builder/data/fixtures/price';

import './CheckoutDemo.scss';

const messages = defineMessages({
  checkout: {
    id: 'docsApp.integrationBuilder.demo.checkout.checkout',
    defaultMessage: 'Checkout',
  },
  errorHeader: {
    id: 'docsApp.integrationBuilder.demo.checkout.errorHeader',
    defaultMessage: 'Error',
  },
  errorBody: {
    id: 'docsApp.integrationBuilder.demo.checkout.errorBody',
    defaultMessage:
      'There was an error when loading this demo. Reload the page and try again.',
  },
  completedHeader: {
    id: 'docsApp.integrationBuilder.demo.checkout.completedHeader',
    defaultMessage: 'Checkout Session completed',
  },
  completedBody: {
    id: 'docsApp.integrationBuilder.demo.checkout.completedBody',
    defaultMessage:
      'Listen for the `payment_intent.succeeded` webhook event to know when to fulfill the order.',
  },
  canceledHeader: {
    id: 'docsApp.integrationBuilder.demo.checkout.canceledHeader',
    defaultMessage: 'Checkout Session canceled',
  },
  canceledBody: {
    id: 'docsApp.integrationBuilder.demo.checkout.canceledBody',
    defaultMessage:
      'Create a new Checkout Session if your customer changes their order details before paying.',
  },
  tryAgain: {
    id: 'docsApp.integrationBuilder.demo.checkout.tryAgain',
    defaultMessage: 'Try Again',
  },
});

export const CheckoutDemo = () => {
  const {account} = useAccount();
  const {api} = useApi();
  const [stripe, setStripe] = useState(null);
  const [message, setMessage] = useState(null);
  const {toggles} = useCondition();

  const clearMessage = () => {
    setMessage(null);
    window.history.replaceState({}, '', `${window.location.pathname}`);
  };

  const handleClick = async () => {
    if (!stripe) {
      return;
    }

    try {
      const options = toggles.customize
        ? {
            customer_email: 'customer@example.com',
            submit_type: 'donate',
            shipping_address_collection: {
              allowed_countries: ['US', 'CA'],
            },
            billing_address_collection: 'required',
          }
        : {};

      const session = await api.createCheckoutSession({
        ...options,
        payment_method_types: ['card'],
        line_items: [
          {
            price_data: {
              currency: 'usd',
              product_data: {
                name: 'Stubborn Attachments',
                images: ['https://i.imgur.com/EHyR2nP.png'],
              },
              unit_amount: 2000,
            },
            quantity: 1,
          },
        ],
        mode: 'payment',
        success_url: `${window.location.href}?success=true`,
        cancel_url: `${window.location.href}?canceled=true`,
      });
      if (session) {
        if (session.error) {
          setMessage({
            header: <FormattedMessage {...messages.errorHeader} />,
            body: session.error,
            icon: 'block',
          });
        } else if (session.id) {
          await stripe.redirectToCheckout({sessionId: session.id});
        }
      }
    } catch (error) {
      setMessage({
        header: <FormattedMessage {...messages.errorHeader} />,
        body: <FormattedMessage {...messages.errorBody} />,
        icon: 'block',
      });
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has('success')) {
      setMessage({
        header: <FormattedMessage {...messages.completedHeader} />,
        body: <FormattedMessage {...messages.completedBody} />,
        icon: 'check',
      });
    } else if (urlParams.has('canceled')) {
      setMessage({
        header: <FormattedMessage {...messages.canceledHeader} />,
        body: <FormattedMessage {...messages.canceledBody} />,
        icon: 'block',
      });
    }

    async function load() {
      // Use this Stripe test account so we can use the same predefined SKU as /docs
      const stripeLib = await loadStripe(account.keys.publishable);
      setStripe(stripeLib);
    }
    load();
  }, [account.keys.publishable]);

  return (
    <Box
      className="CheckoutDemo"
      flex={{alignItems: 'center'}}
      style={{minHeight: '170px'}}
    >
      {message ? (
        <Message {...message} clearMessage={clearMessage} />
      ) : (
        <Product handleClick={handleClick} />
      )}
    </Box>
  );
};

const Message = ({header, body, icon, clearMessage}) => {
  return (
    <Group alignItems="center" padding={{horizontal: 32}}>
      <Icon icon={icon} color="white" style={{marginBottom: '12px'}} />{' '}
      <Heading color="white">{header}</Heading>
      <Body color="white">{body}</Body>
      <Box margin={{top: 4}}>
        <Button
          color="white"
          label={<FormattedMessage {...messages.tryAgain} />}
          onClick={clearMessage}
        />
      </Box>
    </Group>
  );
};

const Product = ({handleClick}) => {
  const formatPriceAmount = useFormatPriceAmount();

  const {
    product: {name, description, images},
  } = DEFAULT_PRICE;
  const imageSrc = images?.[0];

  return (
    <section>
      <Box className="product">
        {/* TODO(mfix) figure out how to render image from `products` API (maybe with CreateS3URLFromFile) */}
        {<img src={imageSrc} alt={description} />}
        <Box className="description">
          <h3>{name}</h3>
          <h5>{formatPriceAmount(DEFAULT_PRICE)}</h5>
        </Box>
      </Box>
      <button
        type="button"
        className="CheckoutDemo--button"
        onClick={handleClick}
      >
        <FormattedMessage {...messages.checkout} />
      </button>
    </section>
  );
};
